<template>
  <div class="row clearfix">
    <div class="col-12">
      <div class="card" style="position: relative; z-index: 100; overflow: visible;">
        <div class="body" style="overflow: visible; padding: 20px;">
          <div class="d-flex align-items-center flex-wrap dashboard-filters">
            <strong class="mr-3">Filtros:</strong>

            <!-- Filtro de Usuários: só admin. Não-admin não vê o filtro (dados já escopados ao próprio ramal) -->
            <b-dropdown
              v-if="isAdmin"
              :text="filterUsersLabel"
              :variant="filterUsersActive ? 'primary' : 'outline-secondary'"
              size="sm"
              class="mr-2"
              menu-class="multi-filter-menu"
              @shown="fillUsersList">
              <b-dropdown-form class="multi-filter-form" @submit.prevent>
                <div class="multi-filter">
                  <!-- Busca no servidor por nome (debounce) -->
                  <input
                    v-model="userSearch"
                    type="text"
                    class="multi-filter-search"
                    placeholder="Buscar usuário...">
                  <div v-if="showToggleAllUsers" class="multi-filter-actions">
                    <button type="button" @click="toggleAllUsers">
                      {{ toggleAllUsersLabel }}
                    </button>
                  </div>
                  <div v-if="usersTooMany || tooManyToSelect" class="dashboard-filter-empty">
                    Muitos resultados para selecionar de uma vez. Refine a busca.
                  </div>
                  <div ref="usersList" class="multi-filter-list" @scroll="onUsersScroll">
                    <b-form-checkbox-group
                      v-if="filterUsersOptions.length"
                      v-model="usersModel"
                      :options="filterUsersOptions"
                      stacked />
                    <div v-else-if="!usersLoading && !usersError" class="dashboard-filter-empty">
                      {{ userSearch.trim() ? 'Nenhum usuário encontrado.' : 'Nenhum usuário ativo.' }}
                    </div>
                    <div v-if="usersLoading" class="multi-filter-loading">Carregando...</div>
                    <div v-if="usersError" class="multi-filter-loading">
                      Não foi possível carregar os usuários.
                      <button type="button" class="btn btn-link btn-sm p-0" @click="$emit('load-more-users')">
                        Tentar novamente
                      </button>
                    </div>
                  </div>
                </div>
              </b-dropdown-form>
            </b-dropdown>

            <b-dropdown
              :text="filterDateLabel"
              :variant="filterDateActive ? 'primary' : 'outline-secondary'"
              size="sm"
              class="mr-2"
              menu-class="period-menu"
              ref="periodDropdown">
              <b-dropdown-item
                v-for="option in filterDateOptions"
                :key="option.text"
                @click="selectDate(option.value)">
                {{ option.text }}
              </b-dropdown-item>
              <b-dropdown-form class="period-custom-form" @submit.prevent="applyCustomPeriod">
                <button
                  type="button"
                  class="dropdown-item period-custom-toggle"
                  @click="customPeriodOpen = !customPeriodOpen">
                  Personalizado
                </button>
                <div v-if="customPeriodOpen" class="period-custom">
                  <label class="period-custom-field">De <input type="date" v-model="customFrom" :max="customFromMax" @click="openDatePicker"></label>
                  <label class="period-custom-field">Até <input type="date" v-model="customTo" :min="customFrom" :max="customToMax" @click="openDatePicker"></label>
                  <div v-if="customPeriodError" class="period-custom-error">{{ customPeriodError }}</div>
                  <button type="button" class="btn btn-sm btn-primary" @click="applyCustomPeriod">OK</button>
                </div>
              </b-dropdown-form>
            </b-dropdown>

            <b-dropdown
              :text="filterTypeLabel"
              :variant="filterTypeActive ? 'primary' : 'outline-secondary'"
              size="sm"
              class="mr-2">
              <b-dropdown-item
                v-for="option in filterTypeOptions"
                :key="option.text"
                @click="$emit('change-type', option.value)">
                {{ option.text }}
              </b-dropdown-item>
            </b-dropdown>

            <b-dropdown
              :text="filterStatusLabel"
              :variant="filterStatusActive ? 'primary' : 'outline-secondary'"
              size="sm"
              class="mr-2"
              menu-class="multi-filter-menu status-filter-menu">
              <b-dropdown-form class="multi-filter-form" @submit.prevent>
                <div v-if="filterStatusOptions.length" class="multi-filter">
                  <input
                    v-model="statusSearch"
                    type="text"
                    class="multi-filter-search"
                    placeholder="Buscar status...">
                  <div class="multi-filter-actions">
                    <button type="button" @click="toggleAllStatus">
                      {{ allStatusSelected ? 'Desmarcar todos' : `Selecionar todos (${filterStatusOptions.length})` }}
                    </button>
                  </div>
                  <div class="multi-filter-list">
                    <b-form-checkbox-group
                      v-if="filteredStatusOptions.length"
                      v-model="statusModel"
                      :options="filteredStatusOptions"
                      stacked />
                    <div v-else class="dashboard-filter-empty">Nenhum status encontrado.</div>
                  </div>
                </div>
                <div v-else class="dashboard-filter-empty">Nenhum status disponível.</div>
              </b-dropdown-form>
            </b-dropdown>

            <button
              type="button"
              class="btn btn-sm dashboard-refresh-btn ml-auto"
              :class="{ 'dashboard-refresh-btn--pending': hasPending }"
              @click="$emit('apply')">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
                style="margin-right: 6px; vertical-align: text-bottom;">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {{ hasPending ? 'Aplicar filtros' : 'Atualizar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BDropdown,
  BDropdownForm,
  BDropdownItem,
  BFormCheckboxGroup
} from 'bootstrap-vue/dist/bootstrap-vue.esm.min'
import { formatPeriodLabel } from './dashboard-helpers'

// Limite máximo do período personalizado (mesma regra do Histórico de Chamadas).
const MAX_CUSTOM_PERIOD_DAYS = 30
const DAY_IN_MS = 86400000
// Espera após a última tecla antes de buscar usuários no servidor.
const USER_SEARCH_DEBOUNCE_MS = 300

// Converte 'YYYY-MM-DD' em timestamp UTC; retorna null para valor vazio/incompleto
// (o input nativo pode emitir strings parciais enquanto o usuário digita).
const parseIsoDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  if (!match) return null
  const time = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return isNaN(time) ? null : time
}

const formatIsoDate = (time) => new Date(time).toISOString().slice(0, 10)

export default {
  name: 'DashboardFilters',
  components: {
    BDropdown,
    BDropdownForm,
    BDropdownItem,
    BFormCheckboxGroup
  },
  props: {
    isAdmin: { type: Boolean, default: false },
    filterUsers: { type: Array, default: () => [] },
    filterUsersOptions: { type: Array, default: () => [] },
    // Rótulo de todo ramal já carregado: nomeia o selecionado mesmo fora da lista atual.
    filterUsersLabels: { type: Object, default: () => ({}) },
    filterDate: { type: String, default: '' },
    filterDateOptions: { type: Array, default: () => [] },
    filterType: { type: String, default: '' },
    filterTypeOptions: { type: Array, default: () => [] },
    filterHangupCause: { type: Array, default: () => [] },
    filterStatusOptions: { type: Array, default: () => [] },
    hasPending: { type: Boolean, default: false },
    usersLoading: { type: Boolean, default: false },
    usersError: { type: [Object, Error], default: null },
    // Ainda há páginas a carregar? (o "Selecionar todos" não mostra contagem parcial)
    usersHasMore: { type: Boolean, default: false },
    // A última tentativa de selecionar os resultados passou do teto.
    usersTooMany: { type: Boolean, default: false },
    // Teto de ramais que o pai aceita selecionar de uma vez.
    usersSelectAllLimit: { type: Number, default: 0 }
  },
  data () {
    return {
      customPeriodOpen: false,
      customFrom: '',
      customTo: '',
      customPeriodError: '',
      userSearch: '',
      userSearchTimeout: null,
      statusSearch: ''
    }
  },
  computed: {
    // Limita o calendário nativo: a data início não pode passar da data fim...
    customFromMax () {
      return this.customTo || ''
    },
    // ...e a data fim não pode passar de 30 dias após a início.
    customToMax () {
      const from = parseIsoDate(this.customFrom)
      if (from === null) return ''
      return formatIsoDate(from + MAX_CUSTOM_PERIOD_DAYS * DAY_IN_MS)
    },
    // Mesmo modelo do filtro de Status: vazio = todos (sem filtro), sem checkmark por padrão.
    usersModel: {
      get () { return this.filterUsers },
      set (val) { this.$emit('change-users', val) }
    },
    // Todos marcados? (controla o toggle do botão) — igual ao Status. Com páginas por
    // carregar, não: "todos" ainda inclui quem não apareceu.
    allUsersSelected () {
      return !this.usersHasMore && this.filterUsersOptions.length > 0 &&
        this.filterUsersOptions.every(o => this.filterUsers.indexOf(o.value) >= 0)
    },
    statusModel: {
      get () { return this.filterHangupCause },
      set (val) { this.$emit('change-status', val) }
    },
    // Filtra a lista de status exibida pela busca (seleção continua multi via checkbox).
    filteredStatusOptions () {
      const term = this.statusSearch.trim().toLowerCase()
      if (!term) return this.filterStatusOptions
      return this.filterStatusOptions.filter(o => o.text.toLowerCase().indexOf(term) >= 0)
    },
    // Todos os status selecionados? (controla o toggle do botão)
    allStatusSelected () {
      return this.filterStatusOptions.length > 0 &&
        this.filterStatusOptions.every(o => this.filterHangupCause.indexOf(o.value) >= 0)
    },
    // Esconde o botão quando os resultados já passam do teto do pai: selecioná-los montaria
    // uma URL que o servidor recusa. Vale só com busca (sem busca o botão manda "todos").
    tooManyToSelect () {
      return !!this.userSearch.trim() && this.usersSelectAllLimit > 0 &&
        this.filterUsersOptions.length > this.usersSelectAllLimit
    },
    showToggleAllUsers () {
      return this.filterUsersOptions.length > 0 && (this.allUsersSelected || !this.tooManyToSelect)
    },
    toggleAllUsersLabel () {
      // Com mais páginas por carregar, a contagem seria parcial: omite.
      const count = this.usersHasMore ? '' : ` (${this.filterUsersOptions.length})`
      if (this.userSearch.trim()) {
        return this.allUsersSelected ? 'Desmarcar resultados' : `Selecionar resultados${count}`
      }
      return this.allUsersSelected ? 'Desmarcar todos' : `Selecionar todos${count}`
    },
    filterUsersLabel () {
      if (this.filterUsers.length === 0) return 'Usuários'
      if (this.filterUsers.length === 1) {
        return this.filterUsersLabels[this.filterUsers[0]] || '1 usuário'
      }
      return `${this.filterUsers.length} usuários selecionados`
    },
    filterUsersActive () {
      // Destacado só quando há recorte (igual ao filtro de Status); vazio = todos = neutro.
      return this.filterUsers.length > 0
    },
    filterDateActive () {
      return !!this.filterDate
    },
    filterTypeActive () {
      // Sempre destacado (como o Período): mostra visualmente o tipo atual, default "Ambas".
      return true
    },
    filterStatusActive () {
      return this.filterHangupCause && this.filterHangupCause.length > 0
    },
    filterStatusLabel () {
      if (!this.filterHangupCause || this.filterHangupCause.length === 0) return 'Status da chamada'
      if (this.filterHangupCause.length === 1) return this.filterHangupCause[0]
      return `${this.filterHangupCause.length} status selecionados`
    },
    filterDateLabel () {
      return formatPeriodLabel(this.filterDate, this.filterDateOptions)
    },
    filterTypeLabel () {
      // Sempre mostra o valor selecionado (default "Ambas"), nunca o placeholder genérico.
      const opt = this.filterTypeOptions.find(o => o.value === this.filterType)
      return opt ? opt.text : 'Ambas'
    }
  },
  watch: {
    // Debounce: a busca vai ao servidor só depois que o usuário para de digitar.
    userSearch (term) {
      clearTimeout(this.userSearchTimeout)
      this.userSearchTimeout = setTimeout(() => this.$emit('search-users', term), USER_SEARCH_DEBOUNCE_MS)
    },
    // Terminou de carregar uma página: se ela não encheu a lista (sem barra de rolagem),
    // o scroll nunca dispararia — pede a próxima até rolar ou acabar.
    usersLoading (loading) {
      if (!loading) this.$nextTick(this.fillUsersList)
    }
  },
  beforeDestroy () {
    clearTimeout(this.userSearchTimeout)
  },
  methods: {
    // Toggle igual ao de Status: todos marcados → desmarca os exibidos (vazio = sem filtro
    // = todos); senão → o pai carrega as páginas que faltam e marca todos do termo atual.
    toggleAllUsers () {
      if (this.allUsersSelected) {
        const shown = this.filterUsersOptions.map(o => o.value)
        this.$emit('change-users', this.filterUsers.filter(v => shown.indexOf(v) < 0))
      } else {
        // O debounce pode não ter disparado: garante que o pai já está no termo atual,
        // senão ele selecionaria pela busca anterior.
        clearTimeout(this.userSearchTimeout)
        this.$emit('search-users', this.userSearch)
        this.$emit('select-all-users')
      }
    },
    // Infinite scroll: ao chegar perto do fim da lista, pede a próxima página de usuários.
    onUsersScroll (event) {
      const el = event.target
      if (!this.usersError && el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
        this.$emit('load-more-users')
      }
    },
    // Lista visível e sem rolagem? Pede mais. clientHeight 0 = dropdown fechado (não mede).
    fillUsersList () {
      const el = this.$refs.usersList
      if (!el || el.clientHeight === 0 || this.usersLoading || this.usersError) return
      if (el.scrollHeight - el.clientHeight < 48) {
        this.$emit('load-more-users')
      }
    },
    toggleAllStatus () {
      if (this.allStatusSelected) {
        this.$emit('change-status', [])
      } else {
        this.$emit('change-status', this.filterStatusOptions.map(o => o.value))
      }
    },
    // Abre o calendário nativo ao clicar em qualquer parte do input de data (não só no ícone).
    openDatePicker (event) {
      const input = event.target
      if (input && typeof input.showPicker === 'function') {
        try {
          input.showPicker()
        } catch (e) {
          // showPicker pode lançar fora de um gesto do usuário; ignora.
        }
      }
    },
    selectDate (value) {
      this.customPeriodOpen = false
      this.customPeriodError = ''
      this.$emit('change-date', value)
    },
    applyCustomPeriod () {
      const from = parseIsoDate(this.customFrom)
      const to = parseIsoDate(this.customTo)
      if (from === null || to === null) {
        this.customPeriodError = 'Selecione as duas datas.'
        return
      }
      if (to < from) {
        this.customPeriodError = 'A data fim não pode ser anterior à início.'
        return
      }
      // O limite vale mesmo se o usuário digitar as datas direto (ignorando o min/max do input).
      if ((to - from) / DAY_IN_MS > MAX_CUSTOM_PERIOD_DAYS) {
        this.customPeriodError = `O intervalo máximo é de ${MAX_CUSTOM_PERIOD_DAYS} dias.`
        return
      }
      this.customPeriodError = ''
      const value = JSON.stringify({
        name: 'custom',
        startedAt: this.customFrom + 'T00:00:00.000Z',
        endedAt: this.customTo + 'T23:59:59.999Z',
        refreshTime: 300,
        refreshCount: 300
      })
      if (this.$refs.periodDropdown) {
        this.$refs.periodDropdown.hide()
      }
      this.$emit('change-date', value)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Dashboard/DashboardFilters.vue